import {
  allRequest,
  deleteRequest,
  getRequest,
  putRequest,
  REQUEST_METHOD_GET
} from "../../../../middlewares/api/actions";
import {
  deleteMappingSetUrl,
  getCategorySchemeUrl,
  getDataflowUrl,
  getDeleteArtefactUrl,
  getMappingSetsUrl,
  getUpdateArtefactsUrl,
  getCheckRunAsAdminPremissionUrl
} from "../../../../constants/urls";
import _ from "lodash";
import {
  getArtefactTripletFromUrn,
  getSdmxJsonFromSdmxJsonStructures,
  getSdmxJsonStructureFromArtefact,
  SDMX_JSON_CATEGORISATION_LIST_KEY
} from "../../../../utils/sdmxJson";
import { Modal } from "antd";

export const CATEGORY_SCHEMES_AND_DATAFLOWS_TREE_READ_MAPPING_SET = 'CATEGORY_SCHEMES_AND_DATAFLOWS_TREE_READ_MAPPING_SET';
export const CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISE_MAPPING_SET = 'CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISE_MAPPING_SET';
export const CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISATION_DELETE_MAPPING_SET = 'CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISATION_DELETE_MAPPING_SET';
export const CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_LANGUAGE_CHANGE_MAPPING_SET = 'CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_LANGUAGE_CHANGE_MAPPING_SET';
export const CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_TO_CATEGORIZE_MAPPING_SET = 'CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_TO_CATEGORIZE_MAPPING_SET';
export const DATAFLOW_FORM_SHOW_MAPPING_SET = 'DATAFLOW_FORM_SHOW_MAPPING_SET';
export const WIZARD_SHOW_MAPPING_SET = 'WIZARD_SHOW_MAPPING_SET';
export const DATAFLOW_EDIT_VIEW_SHOW = 'DATAFLOW_EDIT_VIEW_SHOW';
export const MAPPING_SET_WIZARD_DELETE_MAPPING_SET = 'MAPPING_SET_WIZARD_DELETE_MAPPING_SET';

export const deleteMappingSet = (mappingSetId, lang, categorySchemePermissions) => ({
      ...deleteRequest(
          MAPPING_SET_WIZARD_DELETE_MAPPING_SET,
          deleteMappingSetUrl(mappingSetId),
        t => ({
          success: t('scenes.mappingHandler.MappingSet.mappingSetDeleteSuccessful', {id: mappingSetId})
        }),
          lang,
          categorySchemePermissions
)
});


export const showViewEditDataflow = (dataflowTriplet) => ({
  ...getRequest(
    DATAFLOW_EDIT_VIEW_SHOW,
    getDataflowUrl(dataflowTriplet)),
  dataflowTriplet
});

export const showWizard = (dataflowTriplet) => ({
  ...getRequest(
    WIZARD_SHOW_MAPPING_SET,
    getCheckRunAsAdminPremissionUrl()),
    dataflowTriplet
});

export const readCategorySchemesAndDataflowsTree = (t, lang, dataLanguages, categorySchemePermissions, showOnlyCategoriesWithDataflows = false) => ({
  ...allRequest(
    CATEGORY_SCHEMES_AND_DATAFLOWS_TREE_READ_MAPPING_SET,
    [REQUEST_METHOD_GET, REQUEST_METHOD_GET, REQUEST_METHOD_GET],
    [getCategorySchemeUrl(null, true), getDataflowUrl(), getMappingSetsUrl()]
  ),
	t,
  lang,
	dataLanguages,
  categorySchemePermissions,
  showOnlyCategoriesWithDataflows,
});

export const categoriseCategorySchemesAndDataflowsDataflow = (
  dataflow,
  newCategoryUrn,
  categorisations,
  to,
  fromSisterDfOrderedCategorisations,
  toSisterDfOrderedCategorisations,
  lang,
  orderAnnotationType,
  agencyPermissions,
  t
) => {

  if (!dataflow.categoryUrn) {

    // dataflow was uncategorised

    return addCategorisationToDataflow(
      dataflow,
      newCategoryUrn,
      orderAnnotationType,
      lang,
      to,
      toSisterDfOrderedCategorisations,
      agencyPermissions,
      t);
  } else {

    // dataflow was categorised

    return categoriseDataflow(
      dataflow,
      newCategoryUrn,
      categorisations,
      orderAnnotationType,
      lang,
      to,
      fromSisterDfOrderedCategorisations,
      toSisterDfOrderedCategorisations,
      agencyPermissions,
      t
    );
  }
};


const categoriseDataflow = (
  dataflow,
  newCategoryUrn,
  categorisations,
  orderAnnotationType,
  lang,
  to,
  fromSisterDfOrderedCategorisations,
  toSisterDfOrderedCategorisations,
  agencyPermissions,
  t
) => {

  const newCategorisation =
    _.find(categorisations, cat => cat.source === dataflow.urn && cat.target === dataflow.categoryUrn);
  newCategorisation.target = newCategoryUrn;

  newCategorisation.autoAnnotations = [
    ...(newCategorisation.autoAnnotations || []).filter((ann) => (ann && ann.type !== orderAnnotationType)),
    {
      type: orderAnnotationType,
      text: {
        ...((newCategorisation.autoAnnotations || []).find((ann) => (ann && ann.type === orderAnnotationType)) || {}).text || {},
        [lang]: String(to)
      }
    }
  ];

  // sister source df categorisations must have order in 0..N-1

  const newFromSisterDfCategorisations =
    fromSisterDfOrderedCategorisations
      .filter(({ source }) => source !== dataflow.urn)
      .map((categoris, index) => {
        const newOrderAnnot = {
          type: orderAnnotationType,
          text: {
            ...((categoris.autoAnnotations || []).find((ann) => (ann && ann.type === orderAnnotationType)) || {}).text || {},
            [lang]: String(index)
          }
        };
        return {
          ...categoris,
          autoAnnotations: [
            ...(categoris.autoAnnotations || []).filter((ann) => (ann && ann.type !== orderAnnotationType)),
            newOrderAnnot
          ]
        };
      });

  // sister target df categorisations must have order in 0..to-1 or to+1..N

  const newToSisterDfCategorisations =
    toSisterDfOrderedCategorisations
      .filter(({ source }) => source !== dataflow.urn)
      .map((categoris, index) => {
        const newOrderAnnot = {
          type: orderAnnotationType,
          text: {
            ...((categoris.autoAnnotations || []).find((ann) => (ann && ann.type === orderAnnotationType)) || {}).text || {},
            [lang]: String(index < to ? index : (index + 1))
          }
        };
        return {
          ...categoris,
          autoAnnotations: [
            ...(categoris.autoAnnotations || []).filter((ann) => (ann && ann.type !== orderAnnotationType)),
            newOrderAnnot
          ]
        };
      });

  const structures = [
    getSdmxJsonStructureFromArtefact(newCategorisation),
    ...newFromSisterDfCategorisations.map(getSdmxJsonStructureFromArtefact),
    ...newToSisterDfCategorisations.map(getSdmxJsonStructureFromArtefact)
  ];

  let notAllowedAgency = null;

  structures.forEach(({ agencyID }) => {
    if (!notAllowedAgency && !agencyPermissions.includes(agencyID)) {
      notAllowedAgency = agencyID;
    }
  });

  if (notAllowedAgency) {

    Modal.error({ title: t("scenes.metaManager.categorySchemesDataflowsTree.errors.notAllowedAgency.title", { agencyId: notAllowedAgency }) });

    return ({ type: "NOP" });
  }


  return putRequest(
    CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISE_MAPPING_SET,
    getUpdateArtefactsUrl(),
    getSdmxJsonFromSdmxJsonStructures(
      structures,
      SDMX_JSON_CATEGORISATION_LIST_KEY
    ),
    t => ({
      success: t('scenes.metaManager.categorySchemesAndDataflows.messages.categorisation.update.success')
    })
  )
}

export const addCategorisationToDataflow = (
  dataflow,
  newCategoryUrn,
  orderAnnotationType,
  lang,
  to,
  toSisterDfOrderedCategorisations,
  agencyPermissions,
  t
) => {

  const newCategorisationId = 'CAT_' +
    dataflow.id.substr(0, 40) +
    '_' +
    String(_.random(99999)).padStart(5, '0');

  const newCategorisation = {
    id: newCategorisationId,
    agencyID: dataflow.agencyID,
    version: dataflow.version,
    name: {
      en: newCategorisationId
    },
    source: dataflow.urn,
    target: newCategoryUrn,
    autoAnnotations: [
      {
        type: orderAnnotationType,
        text: { [lang]: String(to) }
      }
    ]
  };

  const newToSisterDfCategorisations =
    toSisterDfOrderedCategorisations
      .filter(({ source }) => source !== dataflow.urn)
      .map((categoris, index) => {
        const newOrderAnnot = {
          type: orderAnnotationType,
          text: {
            ...((categoris.autoAnnotations || []).find((ann) => (ann && ann.type === orderAnnotationType)) || {}).text || {},
            [lang]: String(index < to ? index : (index + 1))
          }
        };
        return {
          ...categoris,
          autoAnnotations: [
            ...(categoris.autoAnnotations || []).filter((ann) => (ann && ann.type !== orderAnnotationType)),
            newOrderAnnot
          ]
        };
      });

  const structures = [
    getSdmxJsonStructureFromArtefact(newCategorisation),
    ...newToSisterDfCategorisations.map(getSdmxJsonStructureFromArtefact)
  ];

  let notAllowedAgency = null;

  structures.forEach(({ agencyID }) => {
    if (!notAllowedAgency && !agencyPermissions.includes(agencyID)) {
      notAllowedAgency = agencyID;
    }
  });

  if (notAllowedAgency) {
    Modal.error({ title: t("scenes.metaManager.categorySchemesDataflowsTree.errors.notAllowedAgency.title", { agencyId: notAllowedAgency }) });
    return ({ type: "NOP" });
  }

  return putRequest(
    CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISE_MAPPING_SET,
    getUpdateArtefactsUrl(),
    getSdmxJsonFromSdmxJsonStructures(
      structures,
      SDMX_JSON_CATEGORISATION_LIST_KEY
    ),
    t => ({
      success: t('scenes.metaManager.categorySchemesAndDataflows.messages.categorisation.update.success')
    })
  );

}

export const deleteCategorySchemesAndDataflowsDataflowCategorisation = (dataflow, categorisations) => {
  const categorisation =
    _.find(categorisations, cat => cat.source === dataflow.urn && cat.target === dataflow.categoryUrn);

  return deleteRequest(
    CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISATION_DELETE_MAPPING_SET,
    getDeleteArtefactUrl("Categorisation", getArtefactTripletFromUrn(categorisation.urn)),
    t => ({
      success: t('scenes.metaManager.categorySchemesAndDataflows.messages.categorisation.delete.success')
    })
  )
};

export const changeCategorySchemesAndDataflowsLanguage = () => ({
  type: CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_LANGUAGE_CHANGE_MAPPING_SET,
})

export const setToCategorizeDataflow = dataflow => ({
  type: CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_TO_CATEGORIZE_MAPPING_SET,
  dataflow
});